import type { AxiosApiResponsePromise } from "~/api/apiResponseTypes.ts";
import axiosApi from "~/api/axiosApi.ts";
import type {
	CreateInvoiceDocumentResponseType,
	CreateNonProjectInvoiceData,
	CreateReversalInvoiceData,
	Invoice,
	InvoiceIndexData,
	InvoiceRecipientSummary,
	UpsertInvoiceAddressPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { Order } from "~/modules/project/api/order/orderTypes.ts";

export function indexProcessedInvoices({ dateFrom, dateTo, includeAllOverdue }: {
	dateFrom: string,
	dateTo: string,
	includeAllOverdue: boolean
}): AxiosApiResponsePromise<InvoiceIndexData[]> {

	const params = {
		dateFrom,
		dateTo,
		includeAllOverdue,
	};

	return axiosApi.get("invoices", { params });
}

export function indexProjectsInvoices(projectId: string): AxiosApiResponsePromise<Invoice[]> {
	return axiosApi.get(`projects/${projectId}/invoices`);
}


export function createNonProjectInvoice(invoiceData: CreateNonProjectInvoiceData): AxiosApiResponsePromise<{
	newInvoiceId: string;
}> {
	return axiosApi.post(`invoices`, invoiceData);
}

export function showInvoice({ invoiceId }: { invoiceId: string }): AxiosApiResponsePromise<Invoice> {
	return axiosApi.get(`/invoices/${invoiceId}`);
}

export function advanceInvoiceStatus(invoiceId: string, invoiceStatusId: string): AxiosApiResponsePromise<void> {
	return axiosApi.patch(`/invoices/${invoiceId}/status/advance`, { invoiceStatusId });
}

export function revertInvoiceStatus(invoiceId: string, invoiceStatusId: string): AxiosApiResponsePromise<void> {
	return axiosApi.patch(`/invoices/${invoiceId}/status/revert`, { invoiceStatusId });
}

export function updateDeliveryPeriod({ invoiceId, deliveryEndDate, deliveryStartDate }: {
	invoiceId: string,
	deliveryEndDate: string,
	deliveryStartDate: string,
}): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/delivery-period`, { deliveryEndDate, deliveryStartDate });
}

export function updateNonProjectInvoiceDescriptionPeriod(invoiceId: string,
	description: string): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/description`, { description });
}

export function updateInvoicePaymentDetails({ invoiceId, updateData }: {
	invoiceId: string,
	updateData: {
		invoicePaidAt: string | null,
		invoiceDeliveryDate: string | null,
		paymentTargetDays: number
	}
}): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/payment-details`, updateData);
}

export function updateInvoiceTotals({ invoiceId, updateData }: {
	invoiceId: string,
	updateData: {
		subtotalCents: number;
		travelCostCentsBeforeVat: number;
		vatPercentage: number;
	}
}): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/totals`, updateData);
}

export function updateInvoiceRecipientInformation({ invoiceId, recipientInformation }: {
	invoiceId: string,
	recipientInformation: string | null,
}): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/recipient-information`, { recipientInformation });
}

export function updateInvoiceSenderInformation({ invoiceId, senderInformation }: {
	invoiceId: string,
	senderInformation: string | null,
}): AxiosApiResponsePromise<Invoice> {
	return axiosApi.patch(`/invoices/${invoiceId}/sender-information`, { senderInformation });
}

export function assignOrderToInvoice(invoiceId: string, orderId: string): AxiosApiResponsePromise<{
	assignedOrderIds: string[];
}> {
	return axiosApi.post(`/invoices/${invoiceId}/order/${orderId}`);
}

export function getAssignableOrdersForInvoice(invoiceId: string): AxiosApiResponsePromise<Order[]> {
	return axiosApi.get(`/invoices/${invoiceId}/assignable-orders`);
}

export function generateInvoiceFile(invoiceId: string): AxiosApiResponsePromise<CreateInvoiceDocumentResponseType> {
	return axiosApi.post(`/invoices/${invoiceId}/invoice-document`, { invoiceId });
}

export function createReversalInvoice(data: CreateReversalInvoiceData): AxiosApiResponsePromise<{
	reversalInvoiceId: string;
}> {
	return axiosApi.post(`/invoices/reversal`, data);
}

export function deleteInvoice(invoiceId: string): AxiosApiResponsePromise<void> {
	return axiosApi.delete(`/invoices/${invoiceId}`);
}

export function getInvoiceRecipientSummary(invoiceId: string): AxiosApiResponsePromise<InvoiceRecipientSummary> {
	return axiosApi.get(`/invoices/${invoiceId}/invoice-recipients-summary`);
}

export function updateTimeTrackingsEditableStatus(
	invoiceId: string,
	timeTrackingsEditable: boolean,
): AxiosApiResponsePromise<string> {
	return axiosApi.patch(`invoices/${invoiceId}/time-trackings-editable-status`, { timeTrackingsEditable });
}

export function upsertInvoiceAddress(
	invoiceId: string,
	payload: UpsertInvoiceAddressPayload,
): AxiosApiResponsePromise<string> {
	return axiosApi.patch(`invoices/${invoiceId}/address`, { ...payload });
}